import React, { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import Route from 'enum/route.enum';
import { Container } from './styles';
import { ContentContainer, OuterContentContainer } from 'design-tokens/warwick/common';
import HeaderBarComponent from './components/HeaderBar';
import PageHeader from './components/PageHeader';
import LoadImage from 'components/common/loadImage/loadImage';
import { translate as t } from 'utils/i18n';
import Storages from 'storages';
import StorageKeys from 'enum/storages.enum';
import { getImageAuthURLAsync } from 'components/main/common/utils';

import locationIcon from 'assets/images/location.svg';

import DeviceData from 'drivers/deviceData';

import {
  InfoContentBody,
  InfoBody,
  HotelCard,
  HotelCardHeader,
  HotelCardDivider,
  HotelName,
  HotelAddress,
  HotelContentRow,
  ManagerCard,
  ManagerTopRow,
  ManagerAvatar,
  ManagerNameGroup,
  ManagerName,
  ManagerTitle,
  ManagerMessage,
  AboutSection,
  AboutHeading,
  AboutParagraphGroup,
  AboutParagraph,
  StatsColumn,
  StatChip,
  StatIconBox,
  StatTextGroup,
  StatValue,
  StatLabel,
  AmenitiesSection,
  AmenitiesSectionHeading,
  AmenitiesRow,
  AmenityCard,
  AmenityIconBox,
  AmenityName,
  AmenityDescription,
} from './components/InfoCard/styles';
import CachedImg from 'components/common/CachedImg/CachedImg';

// ---------------------------------------------------------------------------
// Resolve the hotel_info profile from the cached categories + profiles.
// Finds the first category with layout_type === 'hotel_info', reads its
// stored profile, and maps it to the hotelInfo shape consumed by <Template />.
// ---------------------------------------------------------------------------
async function loadHotelInfoFromCache() {
  const categories = Storages.MainStorage.getItem(StorageKeys.Keys.PROFILECATEGORY) || [];
  const languageId = Storages.MainStorage.getItem(StorageKeys.Keys.LANGUAGEID);

  const hotelCategory = categories.find((cat) => cat.layout_type === 'hotel_info');
  if (!hotelCategory) return null;

  const storedProfile = Storages.MainStorage.getItem(StorageKeys.Keys.PROFILE + hotelCategory.id);
  if (!storedProfile) return null;

  const localeEntry = storedProfile.profile_desc_locale.find((l) => l.language_id === languageId);
  if (!localeEntry) return null;

  let parsed;
  try {
    parsed = JSON.parse(localeEntry.locale_description);
  } catch {
    return null;
  }

  const rawAssets = storedProfile.profile_assets || {};

  // Resolve all asset URLs (API key injection, AWS signing) in parallel
  const [managerPhoto, resolvedAssets] = await Promise.all([
    getImageAuthURLAsync(storedProfile.image),
    Promise.all(
      Object.entries(rawAssets).map(async ([key, url]) => [key, await getImageAuthURLAsync(url)])
    ).then(Object.fromEntries),
  ]);

  return {
    hotel: {
      name: hotelCategory.category_title,
      address: parsed.hotel_address || '',
    },
    manager: {
      photo: managerPhoto,
      name: parsed.manager_name || '',
      title: parsed.manager_title || '',
      message: parsed.manager_bio || '',
    },
    about: {
      heading: t('Portal.portal'),
      paragraphs: Array.isArray(parsed.about_paragraphs) ? parsed.about_paragraphs : [],
    },
    stats: (parsed.stats || []).map((s) => ({
      id: s.icon_key,
      value: s.value,
      label: s.label,
      icon: resolvedAssets[s.icon_key] || null,
    })),
    amenities: (parsed.amenities || []).map((a) => ({
      id: a.icon_key,
      icon: resolvedAssets[a.icon_key] || null,
      name: a.name,
      description: a.description,
    })),
  };
}

// ---------------------------------------------------------------------------
// Presentation component — receives hotelInfo (from API).
// ---------------------------------------------------------------------------
const Template = ({ hotelInfo, onBack }) => {
  const { hotel, manager, about, stats, amenities } = hotelInfo;

  return (
    <Container>
      <HeaderBarComponent />
      <OuterContentContainer>
        <ContentContainer>
          <PageHeader title={t('Portal.organization_info').replace("{organization}", Storages.getOrganizationName())} onBack={onBack} autoFocus={true} />

          {/* InfoContentBody extends ContentBody to shrink-wrap content height */}
          <InfoContentBody>
            <InfoBody>
              {/* ── Section 1: Hotel overview ──────────────────────────── */}
              <HotelCard>
                <HotelCardHeader>
                  <HotelName>{hotel.name}</HotelName>
                  <HotelAddress>
                    <img src={locationIcon} alt='location' />
                    <span>{hotel.address}</span>
                  </HotelAddress>
                </HotelCardHeader>

                <HotelCardDivider />

                <HotelContentRow>
                  {/* Manager card */}
                  <ManagerCard>
                    {/* Row 1: avatar + name/title */}
                    <ManagerTopRow>
                      {manager.photo && (
                        <ManagerAvatar>
                          <LoadImage image_url={manager.photo} alt={manager.name} />
                        </ManagerAvatar>
                      )}
                      <ManagerNameGroup>
                        <ManagerName>{manager.name}</ManagerName>
                        <ManagerTitle>{manager.title}</ManagerTitle>
                      </ManagerNameGroup>
                    </ManagerTopRow>

                    {/* Row 2: message */}
                    <ManagerMessage>{manager.message}</ManagerMessage>
                  </ManagerCard>

                  {/* About Hotel section */}
                  <AboutSection>
                    <AboutHeading>{about.heading}</AboutHeading>
                    <AboutParagraphGroup>
                      {about.paragraphs.map((text, i) => (
                        <AboutParagraph key={i}>{text}</AboutParagraph>
                      ))}
                    </AboutParagraphGroup>
                  </AboutSection>

                  {/* Stats column */}
                  <StatsColumn>
                    {stats.map((stat) => (
                      <StatChip key={stat.id}>
                        <StatIconBox>
                          <CachedImg src={stat.icon} alt=''></CachedImg>
                        </StatIconBox>
                        <StatTextGroup>
                          <StatValue>{stat.value}</StatValue>
                          <StatLabel>{stat.label}</StatLabel>
                        </StatTextGroup>
                      </StatChip>
                    ))}
                  </StatsColumn>
                </HotelContentRow>
              </HotelCard>

              {/* ── Section 2: Amenities ───────────────────────────────── */}
              <AmenitiesSection>
                <AmenitiesSectionHeading>{t('Portal.premium_services')}</AmenitiesSectionHeading>
                <AmenitiesRow>
                  {amenities.map((amenity) => (
                    <AmenityCard key={amenity.id}>
                      <AmenityIconBox>
                        {amenity.icon && <CachedImg src={amenity.icon} alt=''></CachedImg>}
                      </AmenityIconBox>
                      <AmenityName>{amenity.name}</AmenityName>
                      <AmenityDescription>{amenity.description}</AmenityDescription>
                    </AmenityCard>
                  ))}
                </AmenitiesRow>
              </AmenitiesSection>
            </InfoBody>
          </InfoContentBody>
        </ContentContainer>
      </OuterContentContainer>
    </Container>
  );
};

// ---------------------------------------------------------------------------
// Route component — owns navigation and data-loading state.
// ---------------------------------------------------------------------------
const WarwickInfo = () => {
  const [redirectBack, setRedirectBack] = useState(false);
  const [hotelInfo, setHotelInfo] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let tvKeyControls = null;

    DeviceData.getTVControl().then((controls) => {
      tvKeyControls = controls;
    });

    const onKeyPress = (event) => {
      if (!tvKeyControls) return;
      if (event.keyCode === tvKeyControls.KEY_BACK) {
        event.preventDefault();
        setRedirectBack(true);
      }
    };

    window.addEventListener('keydown', onKeyPress);

    loadHotelInfoFromCache()
      .then((data) => {
        if (data) {
          setHotelInfo(data);
        } else {
          setLoadError(true);
        }
      })
      .catch(() => setLoadError(true));

    return () => {
      window.removeEventListener('keydown', onKeyPress);
    };
  }, []);

  if (redirectBack) {
    return <Navigate to={Route.route.LANDING} />;
  }

  if (loadError) {
    return (
      <Container>
        <HeaderBarComponent />
        <OuterContentContainer>
          <ContentContainer>
            <PageHeader
              title={t('Portal.organization_info').replace("{organization}", Storages.getOrganizationName())}
              onBack={() => setRedirectBack(true)}
              autoFocus={true}
            />
            <p style={{ textAlign: 'center', color: '#c0392b', marginTop: '40px' }}>
              {t('Portal.portal_error')}
            </p>
          </ContentContainer>
        </OuterContentContainer>
      </Container>
    );
  }

  if (!hotelInfo) {
    return null;
  }

  return <Template hotelInfo={hotelInfo} onBack={() => setRedirectBack(true)} />;
};

export default WarwickInfo;
